import {
  Alert,
  Descriptions,
  Empty,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import { formatDateTime } from "../ai-reports/status";
import {
  ACTION_STATUS,
  CANAIS,
  CONVERSATION_STATUS,
  MESSAGE_STATUS,
  StatusTag,
  formatNumber,
  formatUsd,
} from "./labels";

const { Text, Paragraph } = Typography;

export function rotuloDaChamada(chamada) {
  if (chamada.filial) return `${chamada.nome} · ${chamada.filial}`;
  if (chamada.filial_id && !chamada.ok) {
    return `${chamada.nome} · filial ${chamada.filial_id}`;
  }
  return chamada.nome;
}

function ToolCallTag({ chamada }) {
  const argumentos = chamada.filial_id
    ? { filial_id: chamada.filial_id, ...(chamada.args || {}) }
    : chamada.args || {};
  const detalhe = JSON.stringify(argumentos, null, 2);
  return (
    <Tooltip
      title={
        <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 11 }}>
          {chamada.erro ? `erro: ${chamada.erro}\n` : ""}
          {detalhe}
        </pre>
      }>
      <Tag color={chamada.ok ? "blue" : "red"}>{rotuloDaChamada(chamada)}</Tag>
    </Tooltip>
  );
}

function Mensagem({ mensagem }) {
  const doUsuario = mensagem.papel === "user";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: doUsuario ? "flex-end" : "flex-start",
        gap: 4,
      }}>
      <div
        style={{
          maxWidth: "85%",
          background: doUsuario ? "#1a1a1a" : "#f3f4f6",
          color: doUsuario ? "#fff" : "#111827",
          padding: "8px 12px",
          borderRadius: doUsuario ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
          fontSize: 13,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
        }}>
        {mensagem.purgada ? <i>conteúdo purgado</i> : mensagem.texto}
      </div>
      <Space size={4} wrap style={{ fontSize: 11, color: "#6b7280" }}>
        <span>{formatDateTime(mensagem.created_at)}</span>
        {mensagem.status !== "concluido" && (
          <StatusTag status={mensagem.status} mapa={MESSAGE_STATUS} />
        )}
        {!doUsuario && mensagem.modelo && <span>· {mensagem.modelo}</span>}
        {!doUsuario && mensagem.input_tokens > 0 && (
          <span>
            · {formatNumber(mensagem.input_tokens)} /{" "}
            {formatNumber(mensagem.output_tokens)} tokens ·{" "}
            {formatUsd(mensagem.custo_usd)}
          </span>
        )}
        {doUsuario && mensagem.latencia_ms !== null && (
          <span>· {(mensagem.latencia_ms / 1000).toFixed(1)} s</span>
        )}
        {doUsuario && mensagem.tentativas > 1 && (
          <span>· {mensagem.tentativas} tentativas</span>
        )}
      </Space>
      {!doUsuario && (mensagem.tool_calls || []).length > 0 && (
        <Space size={2} wrap>
          {mensagem.tool_calls.map((chamada, indice) => (
            <ToolCallTag key={indice} chamada={chamada} />
          ))}
        </Space>
      )}
      {!doUsuario && (mensagem.guardrail_findings || []).length > 0 && (
        <Text type="danger" style={{ fontSize: 11 }}>
          Guardrail:{" "}
          {mensagem.guardrail_findings
            .map((a) => `${a.tipo}: ${a.valor}`)
            .join("; ")}
        </Text>
      )}
      {doUsuario && mensagem.error_class && (
        <Text type="danger" style={{ fontSize: 11 }}>
          {mensagem.error_class}
          {mensagem.error_message ? `: ${mensagem.error_message}` : ""}
        </Text>
      )}
    </div>
  );
}

export default function ConversationModal({ conversationId, onClose, zIndex }) {
  const { getAgentConversationDetail } = useApi();
  const { data, isFetching, error } = useQuery({
    queryKey: ["agent-conversation-detail", conversationId],
    queryFn: () => getAgentConversationDetail(conversationId),
    enabled: conversationId !== null,
    staleTime: 0,
  });

  const conversa = data?.conversa;

  return (
    <Modal
      open={conversationId !== null}
      onCancel={onClose}
      footer={null}
      width="min(1100px, 94vw)"
      zIndex={zIndex}
      centered
      destroyOnClose
      styles={{
        body: { maxHeight: "78vh", overflowY: "auto", paddingRight: 8 },
      }}
      title={
        conversa
          ? `Conversa ${conversa.id} · ${conversa.cliente} / ${conversa.filial || "sem filial usada"}`
          : "Conversa"
      }>
      {isFetching && !data && <Spin />}
      {error && <Alert type="error" message="Erro ao carregar a conversa." />}
      {conversa && (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Descriptions size="small" column={3} bordered>
            <Descriptions.Item label="Usuário">
              {conversa.usuario || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Canal">
              {CANAIS[conversa.canal] || conversa.canal}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <StatusTag status={conversa.status} mapa={CONVERSATION_STATUS} />
            </Descriptions.Item>
            <Descriptions.Item label="Custo">
              {formatUsd(conversa.custo_usd)}
            </Descriptions.Item>
            <Descriptions.Item label="Início">
              {formatDateTime(conversa.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Última mensagem">
              {formatDateTime(conversa.last_message_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Filiais usadas" span={3}>
              {(conversa.filiais || []).length
                ? conversa.filiais.map((filial) => filial.nome).join(", ")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Tokens" span={3}>
              {formatNumber(conversa.input_tokens)} de entrada /{" "}
              {formatNumber(conversa.output_tokens)} de saída
            </Descriptions.Item>
          </Descriptions>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {data.mensagens.length === 0 ? (
              <Empty description="Sem mensagens." />
            ) : (
              data.mensagens.map((mensagem) => (
                <Mensagem key={mensagem.id} mensagem={mensagem} />
              ))
            )}
          </div>
          <div>
            <Text strong>Alterações solicitadas</Text>
            <Table
              size="small"
              rowKey="id"
              dataSource={data.acoes}
              pagination={false}
              locale={{ emptyText: "Nenhuma alteração solicitada." }}
              columns={[
                { title: "Ferramenta", dataIndex: "tool", key: "tool" },
                {
                  title: "Filial",
                  dataIndex: "filial",
                  key: "filial",
                  render: (v) => (v ? <Tag>{v}</Tag> : "-"),
                },
                {
                  title: "Resumo",
                  dataIndex: "resumo",
                  key: "resumo",
                  render: (v, r) => (
                    <div>
                      <Paragraph style={{ margin: 0 }}>{v}</Paragraph>
                      {r.resultado?.erro && (
                        <Text type="danger">{r.resultado.erro}</Text>
                      )}
                    </div>
                  ),
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  render: (s) => <StatusTag status={s} mapa={ACTION_STATUS} />,
                },
                {
                  title: "Executada",
                  key: "executada",
                  render: (_, r) =>
                    r.executed_at ? (
                      <div>
                        {`${formatDateTime(r.executed_at)} · ${r.executed_by || "—"}`}
                        {r.undone_at && (
                          <div>
                            <Text type="secondary">
                              desfeita em {formatDateTime(r.undone_at)}
                            </Text>
                          </div>
                        )}
                      </div>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />
          </div>
        </Space>
      )}
    </Modal>
  );
}
